/**
 * @file porta.ts
 * @description A porta: o endereço por onde a sessão entrou.
 *
 * ## Por que isso virou um conceito
 *
 * A marca chegou a viver no `localStorage`, para sobreviver à navegação
 * depois do login — o caminho perde o slug assim que a pessoa sai de
 * `/entrar/<slug>`. Era estado escondido sem ciclo de vida: não morria no
 * logout, não morria com a sessão, e quem visitasse uma marca uma vez ficava
 * nela até limpar o navegador à mão.
 *
 * A saída não foi guardar melhor, foi precisar menos:
 *
 *  - **Logada**, a marca vem da sessão. O contexto assumido já tem `brandId`,
 *    e desde a A027 ele é necessariamente o da porta — o login filtra os
 *    contextos pela porta e `switch-context` recusa fora dela.
 *  - **Deslogada**, vem do endereço: subdomínio ou `/entrar/<slug>`.
 *  - Entre os dois — deslogada, navegando por telas públicas da mesma marca —
 *    sobra uma janela curta, e só ela usa storage. `sessionStorage`, que
 *    morre com a aba.
 */

/** Onde a janela deslogada guarda a marca. Morre com a aba, de propósito. */
const CHAVE_DA_ABA = 'eva:porta';

export function lerPortaDaAba(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DA_ABA);
  } catch {
    return null;
  }
}

export function gravarPortaDaAba(slug: string | null): void {
  try {
    if (slug === null) sessionStorage.removeItem(CHAVE_DA_ABA);
    else sessionStorage.setItem(CHAVE_DA_ABA, slug);
  } catch {
    // Storage bloqueado: a marca se perde entre telas públicas, e tudo bem.
  }
}

/**
 * Para onde mandar quem saiu — a mesma porta por onde entrou.
 *
 * `/entrar` puro quando o hostname já resolve a marca (subdomínio, o desenho
 * de produção): o endereço carrega a informação. `/entrar/<slug>` quando não,
 * que é o caso do dash-slug em desenvolvimento.
 */
export function caminhoDeEntrada(opts: {
  /** A marca da sessão que está terminando. */
  slug?: string | null;
  /** A detecção pelo hostname já aponta para essa marca? */
  hostResolveAMarca: boolean;
}): string {
  if (opts.hostResolveAMarca || !opts.slug) return '/entrar';
  return `/entrar/${encodeURIComponent(opts.slug)}`;
}
